import { Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, ClipboardList, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PushToggle } from "@/components/PushToggle";
import { Button } from "@/components/ui/button";

export const DashboardLayout = ({ title, children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const links = user?.role === "owner"
    ? [{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "sidebar-dashboard-link" }]
    : [{ to: "/jobs", label: "My Jobs", icon: ClipboardList, testid: "sidebar-jobs-link" }];

  return (
    <div className="min-h-screen bg-[#F3F4F1]">
      <div className="flex">
        <aside className="hidden md:flex flex-col w-60 min-h-screen bg-white border-r border-border p-5 sticky top-0 h-screen" data-testid="dashboard-sidebar">
          <Link to="/" className="flex items-center gap-2 mb-10" data-testid="sidebar-logo-link">
            <img src="/brand/logo.jpg" alt="Blake's Mowing and Odd Jobs logo" className="h-9 w-9 rounded-full object-cover" />
            <span className="font-display font-bold tracking-tight">Blake&apos;s Mowing &amp; Odd Jobs</span>
          </Link>
          <nav className="flex-1 space-y-1">
            {links.map(({ to, label, icon: Icon, testid }) => (
              <Link key={to} to={to} data-testid={testid}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium border-l-4 border-primary bg-secondary text-foreground">
                <Icon className="h-4 w-4" /> {label}
              </Link>
            ))}
          </nav>
          <div className="pt-4 border-t border-border">
            <p className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</p>
            <p className="text-xs text-muted-foreground capitalize mb-3">{user?.role}</p>
            <div className="mb-3">
              <PushToggle />
            </div>
            <Button variant="outline" size="sm" className="w-full rounded-xl" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="h-4 w-4 mr-2" /> Log out
            </Button>
          </div>
        </aside>
        <div className="flex-1 min-w-0">
          <div className="md:hidden flex items-center justify-between bg-white border-b border-border px-4 h-14 sticky top-0 z-40">
            <span className="flex items-center gap-2 min-w-0">
              <img src="/brand/logo.jpg" alt="Blake's Mowing and Odd Jobs logo" className="h-8 w-8 rounded-full object-cover shrink-0" />
              <span className="font-display font-bold text-sm leading-tight truncate">Blake&apos;s Mowing &amp; Odd Jobs</span>
            </span>
            <div className="flex items-center gap-2 shrink-0">
              <PushToggle compact />
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-button-mobile">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <main className="p-4 sm:p-8 max-w-6xl">
            <h1 className="text-2xl sm:text-3xl font-display font-bold tracking-tight mb-6" data-testid="dashboard-title">{title}</h1>
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};
